import type { CustomFile } from '../types';

import getRelativePathData from './getRelativePathData';

// ----------------------------------------------------------------------------

export type TypeGroupedFilesResult = Record<
    string,
    { name: string; files: CustomFile[]; totalSize: number; isFolder: boolean }
>;

// ----------------------------------------------------------------------------

/*
 * Groups a flat file list the way the upload list shows it: one group per root folder, plus one
 * group per loose file.
 *
 * A folder and a loose file can carry the same name, so the key is made unique when they clash
 * while `name` keeps the display value — merging them would upload the file inside the folder's
 * group and label it as a folder. Two folders of the same name are indistinguishable from a flat
 * list (nothing in a file says which drop or which zip it came from), so those still merge.
 */
function groupFilesByRootFolderOrSingle(files: CustomFile[]): TypeGroupedFilesResult {
    const groupedFiles: TypeGroupedFilesResult = {};

    for (const file of files) {
        const rootFolder = getRelativePathData(file)?.rootFolder;
        const name = rootFolder ?? file.name;
        const isFolder = rootFolder != null;

        let key = name;
        let suffix = 1;

        while (key in groupedFiles && groupedFiles[key].isFolder !== isFolder) {
            suffix += 1;
            key = `${name} (${suffix})`;
        }

        if (!(key in groupedFiles)) {
            groupedFiles[key] = { name, files: [], totalSize: 0, isFolder };
        }

        groupedFiles[key].files.push(file);
        groupedFiles[key].totalSize += file.size;
    }

    return groupedFiles;
}

// ----------------------------------------------------------------------------

export default groupFilesByRootFolderOrSingle;
